import React, { useState, useEffect, useCallback } from 'react'
import { Icon, Status, Row, Input } from '@iqueue/ui-kit'
import { t } from '../hooks/useTranslate'

export const actionsList = ['READ', 'WRITE', 'UPDATE', 'DELETE', 'UPDATE_STATUS']

export const resourceTranslation = {
  USER: t('user.permissions.resources.USER'),
  CHAT: t('user.permissions.resources.CHAT'),
  EVENT: t('user.permissions.resources.EVENT'),
  FABRICATOR: t('user.permissions.resources.FABRICATOR'),
  NODE: t('user.permissions.resources.NODE'),
  MESSAGE: t('user.permissions.resources.MESSAGE'),
  OPTION: t('user.permissions.resources.OPTION'),
  PROFILE: t('user.permissions.resources.PROFILE'),
  SENSOR: t('user.permissions.resources.SENSOR'),
  TASK: t('user.permissions.resources.TASK'),
  VERIFICATION: t('user.permissions.resources.VERIFICATION'),
  RESPONSIBILITY: t('user.permissions.resources.RESPONSIBILITY'),
  TOKEN: t('user.permissions.resources.TOKEN'),
  SETTINGS: t('user.permissions.resources.SETTINGS'),
  ROLE: t('user.permissions.resources.ROLE'),
  MODEL: t('user.permissions.resources.MODEL'),
  INSPECTION_LOG: t('user.permissions.resources.INSPECTION_LOG'),
  LICENSE: t('user.permissions.resources.LICENSE'),
}

export const resourceIcons = {
  USER: 'person',
  CHAT: 'chat',
  EVENT: 'event',
  FABRICATOR: 'factory',
  NODE: 'map',
  MESSAGE: 'message',
  OPTION: 'settings',
  PROFILE: 'account_circle',
  SENSOR: 'sensors',
  TASK: 'task_alt',
  VERIFICATION: 'verified',
  RESPONSIBILITY: 'people',
  TOKEN: 'vpn_key',
  SETTINGS: 'settings_applications',
  ROLE: 'security',
  MODEL: 'layers',
  INSPECTION_LOG: 'fact_check',
  LICENSE: 'badge',
}

export const actionTranslation = {
  READ: { label: t('user.permissions.actions.read'), icon: 'visibility' },
  WRITE: { label: t('user.permissions.actions.write'), icon: 'edit' },
  UPDATE: { label: t('user.permissions.actions.update'), icon: 'update' },
  DELETE: { label: t('user.permissions.actions.delete'), icon: 'delete' },
  UPDATE_STATUS: { label: t('user.permissions.actions.update_status'), icon: 'sync_alt' },
}

function PickPermissionTable({ permissionsState }) {
  return <Row>
    <Input
      name={ 'permissions' }
      type={ 'raw' }
      hidden
      value={ permissionsState }
      serialize={ JSON.stringify.bind(null) }
      deserialize={ JSON.parse.bind(null) }
    />
  </Row>
}

export default function PermissionsDragAndDrop({ permissions, data }) {
  const [permissionsState, setPermissionsState] = useState({})

  const initializePermissions = useCallback((data) => {
    const initialPermissions = {}
    Object.keys(resourceTranslation).forEach(resourceKey => {
      initialPermissions[resourceKey] = data?.permissions?.[resourceKey] || []
    })
    setPermissionsState(initialPermissions)
  }, [])

  const toggleAction = useCallback((resource, action) => {
    setPermissionsState((prevState) => {
      const currentActions = prevState[resource] || []
      const actionIndex = currentActions.indexOf(action)

      if (actionIndex > -1) {
        currentActions.splice(actionIndex, 1)
      } else {
        currentActions.push(action)
      }

      return { ...prevState, [resource]: [...currentActions] }
    })
  }, [])

  useEffect(() => {
    if (permissions) {
      setPermissionsState(permissions)
    } else if (data) {
      initializePermissions(data)
    }
  }, [permissions, data])

  return <div className="permissions-container">
    <PickPermissionTable permissionsState={ permissionsState }/>
    <div className="helper-section">
      <div className="helper-header">
        <Icon className="helper-icon">help_outline</Icon>
        <h4>{ t('user.permissions.explanationTitle') }</h4>
      </div>
      <div className="helper-status-info">
        <span className="status-wrapper">
          <Status weak inline error={ false }>
            <Icon>check_circle</Icon>
          </Status>
          <b>{ t('user.permissions.accessAllowed') }</b>
        </span>
        <span className="status-wrapper">
      <Status weak inline error={ true }>
        <Icon>cancel</Icon>
      </Status>
      <b>{ t('user.permissions.accessDenied') }</b>
    </span>
      </div>

      { Object.keys(actionTranslation).map((action) => (
        <div key={ action } className="elem-helper-info">
      <span className="status-wrapper">
        <Status weak inline className="action-item">
          <Icon className="action-icon">{ actionTranslation[action].icon }</Icon>
        </Status>
        <b>{ actionTranslation[action].label }</b>
      </span>
        </div>
      )) }
    </div>
    <h3>{ t('user.permissions.resourcesTitle') }</h3>
    <div className="resources-list">
      { Object.keys(resourceTranslation).map((resource) => (
        <div key={ resource } className="resource-item">
          <div className="resource-info">
            <Icon className={ 'resource-icon' }>{ resourceIcons[resource] }</Icon>
            <b>{ resourceTranslation[resource] }</b>
          </div>
          <div className="status-grid">
            { actionsList.map((action) => {
              return <Status
                key={ action }
                weak
                inline
                error={ !permissionsState[resource]?.includes(action) }
                onClick={ () => toggleAction(resource, action) }
              >
                <Icon>
                  { action === 'READ' && 'visibility' }
                  { action === 'WRITE' && 'edit' }
                  { action === 'UPDATE' && 'update' }
                  { action === 'DELETE' && 'delete' }
                  { action === 'UPDATE_STATUS' && 'sync_alt' }
                </Icon>
              </Status>
            }) }
          </div>
        </div>
      )) }
    </div>
  </div>
}
